import { useQuery, keepPreviousData } from '@tanstack/react-query';
import type { ApiError } from '@/shared/api/ApiError';
import type { Employee } from '../domain/entities/Employee';
import { getEmployeeRepository } from '../infrastructure/repositories/container';
import { employeeKeys } from './employeeKeys';

export const useEmployeesByAccessRoleQuery = (accessRoleId: number | null) =>
  useQuery<Employee[], ApiError>({
    queryKey: employeeKeys.byAccessRole(accessRoleId ?? 0),
    queryFn: () => getEmployeeRepository().listByAccessRole(accessRoleId!),
    enabled: accessRoleId !== null && accessRoleId > 0,
    staleTime: 5 * 60_000,
    gcTime:    30 * 60_000,
    // Keep previously fetched list visible during background refetch — prevents flicker
    placeholderData: keepPreviousData,
    // CRITICAL: Execute queryFn even when offline — composite repo handles Dexie fallback
    networkMode: 'always',
  });

export const useEmployeeByEntityQuery = (idEntity: number | null) =>
  useQuery<Employee | null, ApiError>({
    queryKey: employeeKeys.byEntity(idEntity ?? 0),
    queryFn: () => getEmployeeRepository().findByEntity(idEntity!),
    enabled: idEntity !== null && idEntity > 0,
    // CRITICAL: Execute queryFn even when offline — composite repo, consistent with sibling useEmployeesByAccessRoleQuery
    networkMode: 'always',
    // Short staleTime (30s) ensures enrollment changes are visible quickly while avoiding
    // excessive Dexie reads during rapid navigation. The queryFn reads from Dexie (offline-first),
    // so refetch is cheap but not free.
    staleTime: 30_000,
  });
